import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, InputGroup, Card, Table } from "react-bootstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  API_ENDPOINTS,
  handleKeyDown,
  roundToMoney,
  trimText,
} from "../../../constant/_constant";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import usePostRequest from "../../../hooks/usePostRequest";
import { useSelector } from "react-redux";
import "rsuite/dist/rsuite.css";
import {
  IconCopyMinus,
  IconCurrencyDollar,
  IconGridDots,
  IconLock,
  IconLockOpen2,
  IconPercentage,
} from "@tabler/icons-react";
import ConfirmVendorPopup from "../ConfirmVendorPopup";
import ConfirmProductPopup from "../ConfirmProductPopup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { trimTrailingZeros } from "../../../utils/_helpers";

// ─────────────────────────────────────────────────────────────────────────────
// Yup schema — every card field is mandatory
// vendorPartner and skuId intentionally use .required() here for the empty-
// check; the extra flag-based check is handled separately in the component.
// ─────────────────────────────────────────────────────────────────────────────
const cardSchema = yup.object({
  productType: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .required("Product type is required"),
  vendorPartner: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .required("Vendor is required"),
  productName: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .required("Product name is required"),
  skuId: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .required("SKU ID is required"),
  qty: yup
    .number()
    .transform((v, o) => (o === "" || o == null ? null : v))
    .nullable()
    .typeError("Must be a number")
    .required("Quantity is required")
    .min(1, "At least 1"),
  um: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .required("UM is required"),
  cost: yup
    .number()
    .transform((v, o) => (o === "" || o == null ? null : v))
    .nullable()
    .typeError("Must be a number")
    .required("Aspetto cost is required")
    .min(0, "Must be 0 or more"),
  margin: yup
    .number()
    .transform((v, o) => (o === "" || o == null ? null : v))
    .nullable()
    .typeError("Must be a number")
    .required("Margin is required")
    .min(0, "Must be 0 or more"),
  customerCost: yup
    .number()
    .transform((v, o) => (o === "" || o == null ? null : v))
    .nullable()
    .typeError("Must be a number")
    .required("Customer price is required")
    .min(0, "Must be 0 or more"),
});

const schema = yup.object({ cards: yup.array().of(cardSchema) });
// ─────────────────────────────────────────────────────────────────────────────

function RfqWonPopup({ show, onClose, products, rfqId, onWonSuccess }) {
  const {
    getValues,
    register,
    watch,
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    // Surface yup errors on submit; then re-validate live after first attempt
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const textAreaRefs = useRef([]);
  const suggestionBoxRefs = useRef([]);
  const vendorSuggestionRefs = useRef([]);

  const isCalculating = useRef(false);
  const vendorDebounceRefs = useRef({});
  const skuDebounceRefs = useRef({});

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { fields: cardFields, remove: removeCard } = useFieldArray({
    control,
    name: "cards",
  });

  const [activeCardIndex, setActiveCardIndex] = useState(null);
  const [dataSuggetion, setDataSuggetion] = useState([]);
  const [marginWarnings, setMarginWarnings] = useState({});
  const [vendorWarnings, setVendorWarnings] = useState({});
  const [suggestionData, setSuggestionData] = useState([]);
  const [totalCostBeforeMargin, setTotalCostBeforeMargin] = useState("0.00");
  const [totalCostAfterMargin, setTotalCostAfterMargin] = useState("0.00");
  const [showSkuId, setShowSkuId] = useState(false);
  const [opprId, setOpprId] = useState("");

  // ── Tracks whether submit has been attempted ────────────────────────────────
  // This gates the flag-based red border: we only show "vendor not recognised"
  // / "SKU not recognised" errors after the user first tries to submit.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Vendor popup state ──────────────────────────────────────────────────────
  const [showVendorPopup, setShowVendorPopup] = useState(false);
  const [pendingVendorName, setPendingVendorName] = useState("");
  const [activeVendorIndex, setActiveVendorIndex] = useState(null);

  // ── Product (SKU) popup state ───────────────────────────────────────────────
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [pendingSkuId, setPendingSkuId] = useState("");
  const [activeSkuIndex, setActiveSkuIndex] = useState(null);

  // ── Validation flags ────────────────────────────────────────────────────────
  const [validationFlags, setValidationFlags] = useState([]);

  // ── Valid products (vendorExist=1 & skuExist=1) — hidden from UI but sent in payload ──
  const [validCards, setValidCards] = useState([]);

  const navigate = useNavigate();
  const orderType = useSelector((state) => state.order.orderType);

  const [cardSearch, setCardSearch] = useState(
    cardFields.map(() => ({
      vendor: "",
      description: "",
      sku: "",
      showSuggestions: true,
    })),
  );

  const { postData: fetchVendors } = usePostRequest(API_ENDPOINTS.GET_VENDORS);
  const { postData: fetchProductSearch } = usePostRequest(
    API_ENDPOINTS.PRODUCT_SUGGETION,
  );
  const { postData: updateRfq } = usePostRequest(
    API_ENDPOINTS.UPDATE_PRODUCTS_RFQ,
  );
  const [searchDebounces, setSearchDebounces] = useState({ searchName: "" });

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside all suggestion boxes for products
      const isProductOutside = cardSearch.every((_, index) => {
        const suggestionBox = suggestionBoxRefs.current[index];
        return suggestionBox ? !suggestionBox.contains(event.target) : true;
      });

      if (isProductOutside) {
        // Close all product suggestion boxes and clear suggestions
        setDataSuggetion(null);
        setCardSearch((prev) =>
          prev.map((card) => ({
            ...card,
            showSuggestions: false,
          })),
        );
      }

      // Check if the click is outside all vendor suggestion boxes
      const isVendorOutside = vendorSuggestionRefs.current.every((box) =>
        box ? !box.contains(event.target) : true,
      );

      if (isVendorOutside) {
        setSuggestionData([]);
        setSearchDebounces({ searchName: "" });
      }
    };

    // Add event listener for mousedown
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [cardSearch]);
  // ── Derived helpers ─────────────────────────────────────────────────────────

  const isVendorInvalid = (index) =>
    !!errors?.cards?.[index]?.vendorPartner ||
    (submitAttempted && !watch(`cards[${index}].vendorPartner`)) || // ← empty + attempted
    (submitAttempted &&
      !!watch(`cards[${index}].vendorPartner`) &&
      (validationFlags[index]?.vendorExist ?? 0) === 0);

  const isSkuInvalid = (index) =>
    !!errors?.cards?.[index]?.skuId ||
    (submitAttempted && !watch(`cards[${index}].skuId`)) || // ← empty + attempted
    (submitAttempted &&
      !!watch(`cards[${index}].skuId`) &&
      (validationFlags[index]?.skuExist ?? 0) === 0);

  const vendorErrorMsg = (index) => {
    if (errors?.cards?.[index]?.vendorPartner?.message)
      return errors.cards[index].vendorPartner.message;
    if (submitAttempted && !watch(`cards[${index}].vendorPartner`))
      return "Vendor is required";
    if (
      submitAttempted &&
      !!watch(`cards[${index}].vendorPartner`) &&
      (validationFlags[index]?.vendorExist ?? 0) === 0
    )
      return "NEW Vendor";
    return null;
  };

  const skuErrorMsg = (index) => {
    if (errors?.cards?.[index]?.skuId?.message)
      return errors.cards[index].skuId.message;
    if (submitAttempted && !watch(`cards[${index}].skuId`))
      return "SKU ID is required";
    if (
      submitAttempted &&
      !!watch(`cards[${index}].skuId`) &&
      (validationFlags[index]?.skuExist ?? 0) === 0
    )
      return "NEW SKU";
    return null;
  };

  // vendorPartner: red when yup says empty OR when flag is still 0 after submit
  // const isVendorInvalid = (index) =>
  //   !!errors?.cards?.[index]?.vendorPartner ||
  //   (submitAttempted && (validationFlags[index]?.vendorExist ?? 0) === 0);

  // // skuId: red when yup says empty OR when flag is still 0 after submit
  // const isSkuInvalid = (index) =>
  //   !!errors?.cards?.[index]?.skuId ||
  //   (submitAttempted && (validationFlags[index]?.skuExist ?? 0) === 0);

  // const vendorErrorMsg = (index) => {
  //   if (errors?.cards?.[index]?.vendorPartner?.message)
  //     return errors.cards[index].vendorPartner.message;
  //   if (submitAttempted && (validationFlags[index]?.vendorExist ?? 0) === 0)
  //     return "NEW Vendor";
  //   return null;
  // };

  // const skuErrorMsg = (index) => {
  //   if (errors?.cards?.[index]?.skuId?.message)
  //     return errors.cards[index].skuId.message;
  //   if (submitAttempted && (validationFlags[index]?.skuExist ?? 0) === 0)
  //     return "NEW SKU";
  //   return null;
  // };

  const makeCard = (product) => ({
    // ...existing fields...
    productType:
      orderType === "S" || orderType === "CRK" || orderType === "M"
        ? "inventory"
        : "non-inventory",
  });

  // ── Product suggestions ─────────────────────────────────────────────────────
  useEffect(() => {
    const vendorPartner =
      watch(`cards[${activeCardIndex}].vendorPartner`) || "";
    if (
      activeCardIndex === null ||
      !cardSearch[activeCardIndex]?.showSuggestions
    )
      return;
    const { vendor, sku, description } = cardSearch[activeCardIndex] || {};
    if (!vendor && !sku && !description) return;

    const payload = {
      page,
      limit,
      module_type: "RFQ",
      is_variant: true,
      vendor: vendorPartner?.length > 0 ? vendorPartner : undefined,
      sku: sku?.length > 0 ? sku : undefined,
      description: description?.length > 0 ? description : undefined,
    };
    const t = setTimeout(
      () => fetchProductSearch(payload, setDataSuggetion),
      500,
    );
    return () => clearTimeout(t);
  }, [cardSearch, page, limit, activeCardIndex]);

  // ── Vendor suggestions ──────────────────────────────────────────────────────
  useEffect(() => {
    if (searchDebounces?.searchName?.length > 0)
      fetchVendors({ search: searchDebounces.searchName }, setSuggestionData);
  }, [searchDebounces]);

  // ── Seed cards from products prop ──────────────────────────────────────────
  useEffect(() => {
    if (!show || !products?.length) return;

    const initialOriginalVendors = {};
    setOpprId(rfqId);

    // Auto-trigger validation display on first open so user immediately
    // sees which fields need attention — no need to click Submit first.
    setSubmitAttempted(true);

    const makeCard = (product) => ({
      skuDescription: product?.description ?? "",
      purchase_description: product?.purchase_description ?? "",
      clin: product?.clin_id ?? "",
      vendorPartner: product?.display_name ?? "",
      skuIdUnique: product?.sku_id ?? "",
      skuId: product?.sku_display_name ?? "",
      rowId: product?.raw_sku_id ?? "",
      productName: product?.name ?? product?.Product?.name ?? "",
      // cost: product?.aspetto_price ?? "",
      cost: trimTrailingZeros(product?.aspetto_price ?? ""),
      margin: trimTrailingZeros(product?.margin_percent ?? ""),
      qty: trimTrailingZeros(product?.quantity ?? 1),
      um: product?.unit_of_measure ?? "EA",
      customerCost: trimTrailingZeros(product?.customer_price ?? ""),
      totalSkuCost: product?.total_price ?? "",
      vendorId: product?.vendor_id ? String(product.vendor_id) : "",
      internalSku: product?.internal_id ?? "",
      variantId: product?.variant_id ?? "",
      CostLock: false,
      marginLock: false,
      customerCostLock: false,
    });

    // Split: invalid products shown in editable cards; valid ones hidden but
    // kept in state so they can be merged back into the payload unchanged.
    const invalidProducts = products.filter(
      (p) => (p?.vendorExist ?? 0) === 0 || (p?.skuExist ?? 0) === 0,
    );
    const validProducts = products.filter(
      (p) => (p?.vendorExist ?? 0) === 1 && (p?.skuExist ?? 0) === 1,
    );

    invalidProducts.forEach((product, index) => {
      initialOriginalVendors[index] =
        product?.Product?.Vendor?.display_name ??
        product?.Vendor?.display_name ??
        "";
    });

    reset({
      cards: invalidProducts.map(makeCard),
      originalVendors: initialOriginalVendors,
    });

    setValidationFlags(
      invalidProducts.map((p) => ({
        display_name: p?.display_name ?? "",
        sku_display_name: p?.sku_display_name ?? "",
        vendorExist: p?.vendorExist ?? 0,
        skuExist: p?.skuExist ?? 0,
      })),
    );

    // Store valid cards separately — they ride along in the payload untouched
    setValidCards(validProducts.map(makeCard));
  }, [show, products, reset]);

  // ── Watch → calculateCosts ──────────────────────────────────────────────────
  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (
        name?.match(/cards\[(\d+)\]\.(qty|cost|margin|customerCost)$/) &&
        !isCalculating.current
      ) {
        isCalculating.current = true;
        calculateCosts(name);
        isCalculating.current = false;
      }
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const allFlagsValid =
    validationFlags.length > 0 &&
    validationFlags.every((f) => f.vendorExist === 1 && f.skuExist === 1);

  // ── Submit ──────────────────────────────────────────────────────────────────
  // onSubmit is only reached when yup passes — still need the flag gate.
  // const onSubmit = (formData) => {
  //   setSubmitAttempted(true);
  //   if (!allFlagsValid) return;

  //   const toApiShape = (product) => ({
  //     vendor_name: product?.vendorPartner ?? null,
  //     sku_id: product?.skuIdUnique // unique id if present, else display name
  //       ? product.skuIdUnique
  //       : (product?.skuId ?? null),
  //     sku_display_name: product?.skuId ?? null,
  //     clin_id: product?.clin ?? null,
  //     name: product?.productName ?? null,
  //     description: product?.skuDescription ?? null,
  //     raw_sku_id: product?.rowId ?? null,
  //     purchase_description: product?.purchase_description ?? null,
  //     quantity: product?.qty ? Number(product.qty) : 0,
  //     aspetto_price: product?.cost ? Number(product.cost) : 0,
  //     customer_price: product?.customerCost ? Number(product.customerCost) : 0,
  //     margin_percent: product?.margin ? Number(product.margin) : 0,
  //     total_price: product?.totalSkuCost ? Number(product.totalSkuCost) : 0,
  //     um: product?.um ?? null,
  //   });

  //   // Merge fixed (valid) products + newly-corrected (invalid) products into one payload
  //   const transformedProducts = [
  //     ...validCards.map(toApiShape), // valid ones go as-is
  //     ...formData.cards.map(toApiShape), // corrected ones from the form
  //   ];

  //   const payload = {
  //     rfq_id: opprId,
  //     products: transformedProducts,
  //   };

  //   const onSuccess = (res) => {
  //     toast.success(res?.message ?? "Products updated successfully");
  //     onClose(); // close RfqWonPopup
  //     onWonSuccess?.(opprId); // tell parent to open CreateOrderEditRfq with this rfqId
  //   };

  //   const onError = (err) => {
  //     toast.error(err?.message ?? "Something went wrong");
  //   };

  //   updateRfq(payload, onSuccess, onError);
  // };

  const onSubmit = (formData) => {
    setSubmitAttempted(true);
    // DELETE: if (!allFlagsValid) return;

    const toApiShape = (product, vendorExist = 1, skuExist = 1) => ({
      vendor_name: product?.vendorPartner ?? null,
      sku_id: product?.skuIdUnique
        ? product.skuIdUnique
        : (product?.skuId ?? null),
      sku_display_name: product?.skuId ?? null,
      clin_id: product?.clin || null,
      name: product?.productName ?? null,
      description: product?.skuDescription || null,
      raw_sku_id: product?.rowId ?? null,
      purchase_description: product?.purchase_description || null,
      ...(product?.internalSku ? { internal_id: product.internalSku } : {}),
      variant_id: product?.variantId || null,
      um: product?.um || null,
      quantity: product?.qty ? Number(product.qty) : 0,
      aspetto_price: product?.cost ? Number(product.cost) : 0,
      customer_price: product?.customerCost ? Number(product.customerCost) : 0,
      margin_percent: product?.margin ? Number(product.margin) : 0,
      total_price: product?.totalSkuCost ? Number(product.totalSkuCost) : 0,
      um: product?.um || null,
      type: product?.productType ?? "non-inventory",
      vendorExists: vendorExist,
      skuExists: skuExist,
    });

    const transformedProducts = [
      // Valid cards always have both flags = 1
      ...validCards.map((p) => toApiShape(p, 1, 1)),
      // Corrected cards carry whatever the current flag state is
      ...formData.cards.map((p, i) =>
        toApiShape(
          p,
          validationFlags[i]?.vendorExist ?? 0,
          validationFlags[i]?.skuExist ?? 0,
        ),
      ),
    ];

    const payload = {
      rfq_id: opprId,
      products: transformedProducts,
    };

    const onSuccess = (res) => {
      toast.success(res?.message ?? "Products updated successfully");
      onClose();
      onWonSuccess?.(opprId);
    };

    const onError = (err) => {
      toast.error(err?.message ?? "Something went wrong");
    };

    updateRfq(payload, onSuccess, onError);
  };

  // onInvalid fires when yup itself fails — mark attempted so flag errors show.
  const onInvalid = () => setSubmitAttempted(true);

  // ── Vendor-field typing → reset vendorExist ─────────────────────────────────
  // const handleSearchByName = (e, index) => {
  //   const val = e.target.value;
  //   setValue(`cards[${index}].vendorPartner`, val);
  //   setSearchDebounces({ searchName: val });
  //   // setSearchDebounces({ searchName: e.target.value });
  //   if (index !== undefined) {
  //     setValidationFlags((prev) => {
  //       const u = [...prev];
  //       if (u[index])
  //         u[index] = {
  //           ...u[index],
  //           display_name: e.target.value,
  //           vendorExist: 0,
  //         };
  //       return u;
  //     });
  //   }
  // };

  const handleSearchByName = (e, index) => {
    const val = e.target.value;
    setValue(`cards[${index}].vendorPartner`, val);
    setSearchDebounces({ searchName: val });
    if (index !== undefined) {
      setValidationFlags((prev) => {
        const u = [...prev];
        if (u[index])
          u[index] = { ...u[index], display_name: val, vendorExist: 0 };
        return u;
      });
    }

    // Debounce: open vendor popup 1s after user stops typing
    clearTimeout(vendorDebounceRefs.current[index]);
    vendorDebounceRefs.current[index] = setTimeout(() => {
      handleAddVendor(val, index);
    }, 1000);
  };

  // ── Vendor popup handlers ───────────────────────────────────────────────────
  const handleAddVendor = (value, index) => {
    if (!value || validationFlags[index]?.vendorExist === 1) return;
    setPendingVendorName(value);
    setActiveVendorIndex(index);
    setShowVendorPopup(true);
  };

  const handleVendorAdded = (response) => {
    toast.success(response?.message);
    const vendorId = response?.id;
    if (activeVendorIndex !== null && vendorId !== undefined) {
      setValue(`cards[${activeVendorIndex}].vendorId`, vendorId, {
        shouldValidate: true,
      });
      setValidationFlags((prev) => {
        const u = [...prev];
        if (u[activeVendorIndex])
          u[activeVendorIndex] = { ...u[activeVendorIndex], vendorExist: 1 };
        return u;
      });
    }
  };

  // console.log("setValidationFlags", validationFlags);

  const handleCloseVendorPopup = () => {
    setShowVendorPopup(false);
    setPendingVendorName("");
    setActiveVendorIndex(null);
  };

  // ── SKU popup handlers ──────────────────────────────────────────────────────
  const handleAddSku = (value, index) => {
    if (!value || validationFlags[index]?.skuExist === 1) return;
    setPendingSkuId(value);
    setActiveSkuIndex(index);
    setShowProductPopup(true);
  };

  const handleProductAdded = (response) => {
    toast.success(response?.message);
    if (activeSkuIndex !== null) {
      // Write the newly created SKU's unique ID back into that card's hidden field
      if (response?.unique_sku_id !== undefined) {
        setValue(
          `cards[${activeSkuIndex}].skuIdUnique`,
          response.unique_sku_id,
          { shouldValidate: true },
        );
      }
      const currentSkuId = getValues(`cards[${activeSkuIndex}].skuId`);
      setValue(`cards[${activeSkuIndex}].skuId`, currentSkuId ?? "", {
        shouldValidate: true,
      });
      setValidationFlags((prev) => {
        const u = [...prev];
        if (u[activeSkuIndex])
          u[activeSkuIndex] = { ...u[activeSkuIndex], skuExist: 1 };
        return u;
      });
    }
  };

  const handleCloseProductPopup = () => {
    setShowProductPopup(false);
    setPendingSkuId("");
    setActiveSkuIndex(null);
  };

  // ── Lock helpers ────────────────────────────────────────────────────────────
  const handleLockChange = (field, index) => {
    const cur = watch(`cards[${index}]`);
    if (cur[field]) {
      setValue(`cards[${index}].${field}`, false);
    } else {
      setValue(`cards[${index}].CostLock`, field === "CostLock");
      setValue(`cards[${index}].marginLock`, field === "marginLock");
      setValue(
        `cards[${index}].customerCostLock`,
        field === "customerCostLock",
      );
    }
  };

  const handleMarginChange = (index, val) =>
    setValue(`cards[${index}].margin`, val);

  // ── Focus / suggestion helpers ──────────────────────────────────────────────
  const handleInputFocus = (index) => {
    setActiveCardIndex(index);
    setCardSearch((prev) =>
      prev.map((c, i) => (i !== index ? { ...c, showSuggestions: false } : c)),
    );
  };

  const adjustTextAreaHeight = (index) => {
    const el = textAreaRefs.current[index];
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const handleSearchChangeDescription = (index, field, value) => {
    const u = [...cardSearch];
    if (field === "description")
      u[index] = {
        ...u[index],
        description: value,
        sku: "",
        vendor: "",
        showSuggestions: true,
      };
    setCardSearch(u);
  };

  // const handleSearchChange = (index, field, value) => {
  //   setValue(`cards[${index}].skuIdUnique`, value);
  //   // Typing in SKU field → reset skuExist
  //   if (field === "sku" && index !== undefined) {
  //     setValidationFlags((prev) => {
  //       const u = [...prev];
  //       if (u[index])
  //         u[index] = { ...u[index], sku_display_name: value, skuExist: 0 };
  //       return u;
  //     });
  //   }
  //   const vendorPartner = watch(`cards[${index}].vendorPartner`) || "";
  //   const u = [...cardSearch];
  //   if (field === "sku")
  //     u[index] = {
  //       ...u[index],
  //       sku: value,
  //       vendor: vendorPartner,
  //       description: "",
  //       showSuggestions: true,
  //     };
  //   else if (field === "vendor")
  //     u[index] = {
  //       ...u[index],
  //       vendor: value,
  //       sku: "",
  //       description: "",
  //       showSuggestions: true,
  //     };
  //   else if (field === "description")
  //     u[index] = {
  //       ...u[index],
  //       description: value,
  //       sku: "",
  //       vendor: "",
  //       showSuggestions: true,
  //     };
  //   setCardSearch(u);
  // };

  const handleSearchChange = (index, field, value) => {
    setValue(`cards[${index}].skuIdUnique`, value);
    if (field === "sku" && index !== undefined) {
      setValidationFlags((prev) => {
        const u = [...prev];
        if (u[index])
          u[index] = { ...u[index], sku_display_name: value, skuExist: 0 };
        return u;
      });

      // Debounce: open SKU popup 1s after user stops typing
      clearTimeout(skuDebounceRefs.current[index]);
      skuDebounceRefs.current[index] = setTimeout(() => {
        handleAddSku(value, index);
      }, 1000);
    }

    const vendorPartner = watch(`cards[${index}].vendorPartner`) || "";
    const u = [...cardSearch];
    if (field === "sku")
      u[index] = {
        ...u[index],
        sku: value,
        vendor: vendorPartner,
        description: "",
        showSuggestions: true,
      };
    else if (field === "vendor")
      u[index] = {
        ...u[index],
        vendor: value,
        sku: "",
        description: "",
        showSuggestions: true,
      };
    else if (field === "description")
      u[index] = {
        ...u[index],
        description: value,
        sku: "",
        vendor: "",
        showSuggestions: true,
      };
    setCardSearch(u);
  };

  // ── Product / vendor selection from dropdown ────────────────────────────────
  const handleProductSelect = (product, index) => {
    const orig = getValues("originalVendors") || {};
    setValue("originalVendors", {
      ...orig,
      [index]: product?.Vendor?.display_name,
    });
    setVendorWarnings((prev) => {
      const u = { ...prev };
      delete u[index];
      return u;
    });

    // Both flags → 1 when selected from suggestion
    setValidationFlags((prev) => {
      const u = [...prev];
      if (u[index])
        u[index] = {
          ...u[index],
          display_name: product?.Vendor?.display_name ?? "",
          sku_display_name: product?.sku_display_name ?? "",
          vendorExist: 1,
          skuExist: 1,
        };
      return u;
    });

    setValue(`cards[${index}].vendorPartner`, product?.Vendor?.display_name, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].productName`, product?.name, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].skuIdUnique`, product?.sku_id, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].skuId`, product?.sku_display_name, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].skuDescription`, product?.description_sales, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].purchase_description`, product?.description, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].um`, product?.uoi, { shouldValidate: true });
    setValue(`cards[${index}].cost`, product?.aspetto_price, {
      shouldValidate: true,
    });
    setValue(
      `cards[${index}].internalSku`,
      product?.internal_id || product?.vendor_sku || "",
      { shouldValidate: true },
    );
    setValue(`cards[${index}].variantId`, product?.variant_id || "", {
      shouldValidate: true,
    });
    setValue(`cards[${index}].customerCost`, product?.customer_price, {
      shouldValidate: true,
    });
    if (product?.customer_price === "0.00") {
      setValue(`cards[${index}].margin`, "8", { shouldValidate: true });
    }

    const ta = textAreaRefs.current[index];
    if (ta) {
      ta.value = product?.description_sales;
      adjustTextAreaHeight(index);
    }

    setCardSearch((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], showSuggestions: false };
      return u;
    });
  };

  const handleSelectVendor = (item, index) => {
    setValue(`cards[${index}].vendorPartner`, item?.display_name, {
      shouldValidate: true,
    });
    setValue(`cards[${index}].vendorId`, item?.id, {
      shouldValidate: true,
    });
    // Vendor flag → 1 on selection
    setValidationFlags((prev) => {
      const u = [...prev];
      if (u[index])
        u[index] = {
          ...u[index],
          display_name: item?.display_name,
          vendorExist: 1,
        };
      return u;
    });
    setSuggestionData([]);
    setSearchDebounces({ searchName: "" });
  };

  // ── Drag & Drop ─────────────────────────────────────────────────────────────
  const handleDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    const cards = [...getValues("cards")];
    const [rem] = cards.splice(source.index, 1);
    cards.splice(destination.index, 0, rem);
    setValue("cards", cards);

    const reindex = (obj) => {
      const out = {};
      Object.keys(obj).forEach((key) => {
        const old = parseInt(key);
        let nw = old;
        if (old === source.index) nw = destination.index;
        else if (
          old >= Math.min(source.index, destination.index) &&
          old <= Math.max(source.index, destination.index)
        )
          nw = old > source.index ? old - 1 : old + 1;
        out[nw] = obj[old];
      });
      return out;
    };

    setValue("originalVendors", reindex(getValues("originalVendors") || {}));
    setMarginWarnings((p) => reindex(p));
    setVendorWarnings((p) => reindex(p));
    setValidationFlags((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, moved);
      return arr;
    });
  };

  const handleRemoveCard = (index) => {
    if (cardFields.length <= 1) return;
    const reindex = (obj) => {
      const out = {};
      Object.keys(obj).forEach((key) => {
        const old = parseInt(key);
        if (old !== index) out[old > index ? old - 1 : old] = obj[old];
      });
      return out;
    };
    setMarginWarnings((p) => reindex(p));
    setVendorWarnings((p) => reindex(p));
    setValue("originalVendors", reindex(getValues("originalVendors") || {}));
    setValidationFlags((prev) => prev.filter((_, i) => i !== index));
    removeCard(index);
    setTimeout(() => calculateCosts(getValues("cards")), 0);
  };

  // ── Cost calculation ────────────────────────────────────────────────────────
  const calculateCosts = (name) => {
    let currentfield = "";
    let currentindex = null;
    if (typeof name === "string") {
      const p = name.match(/cards\[(\d+)\]\.(\w+)$/);
      currentindex = p ? parseInt(p[1]) : null;
      currentfield = p ? p[2] : null;
    }
    let totalBefore = 0,
      totalAfter = 0;
    const cards = getValues("cards") || [];
    const shippingCost = parseFloat(getValues("shippingCost")) || 0;
    const tax = parseFloat(getValues("tax")) || 0;

    cards.forEach((card, index) => {
      if (
        currentfield !== "" &&
        currentindex !== null &&
        currentindex !== index
      ) {
        totalBefore += parseFloat(card.cost || 0) * parseFloat(card.qty || 0);
        totalAfter +=
          parseFloat(card.customerCost || 0) * parseFloat(card.qty || 0);
        return;
      }
      if (currentfield !== "" && currentindex === index) {
        const lockFields = ["customerCostLock", "CostLock", "marginLock"];
        if (lockFields.includes(currentfield)) return;
        const { CostLock: costLock, marginLock, customerCostLock } = card;
        const qty = parseFloat(card.qty) || 0;
        let cost = parseFloat(card.cost) || 0;
        let margin = parseFloat(card.margin) || 0;
        let customerCost = parseFloat(card.customerCost) || 0;
        let totalSkuCost = 0;
        const ci = cards.indexOf(card);

        if (!customerCostLock && !costLock && !marginLock) {
          if (currentfield === "customerCost") {
            const rc = roundToMoney(customerCost);
            totalSkuCost = rc * qty;
            if (cost === 0) {
              margin = 0;
              setValue(`cards[${ci}].cost`, roundToMoney(totalSkuCost));
            } else margin = parseFloat(((rc - cost) / rc) * 100);
            setValue(`cards[${ci}].margin`, roundToMoney(margin));
            setValue(`cards[${ci}].totalSkuCost`, roundToMoney(totalSkuCost));
          } else {
            const rc = roundToMoney(cost / (1 - margin / 100));
            totalSkuCost = rc * qty;
            setValue(`cards[${ci}].customerCost`, rc);
            setValue(`cards[${ci}].totalSkuCost`, roundToMoney(totalSkuCost));
          }
        } else if (customerCostLock) {
          if (currentfield === "cost") {
            margin = parseFloat(((customerCost - cost) / customerCost) * 100);
            totalSkuCost = roundToMoney(customerCost) * qty;
            setValue(`cards[${ci}].margin`, roundToMoney(margin));
          } else if (currentfield === "margin") {
            setValue(
              `cards[${ci}].cost`,
              roundToMoney(customerCost * (1 - margin / 100)),
            );
            totalSkuCost = roundToMoney(customerCost) * qty;
          } else {
            totalSkuCost = roundToMoney(customerCost) * qty;
          }
          setValue(`cards[${ci}].totalSkuCost`, roundToMoney(totalSkuCost));
        } else if (marginLock) {
          if (currentfield === "cost") {
            const rc = roundToMoney(cost / (1 - margin / 100));
            totalSkuCost = rc * qty;
            setValue(`cards[${ci}].customerCost`, rc);
          } else if (currentfield === "customerCost") {
            totalSkuCost = roundToMoney(customerCost) * qty;
            setValue(
              `cards[${ci}].cost`,
              roundToMoney(customerCost * (1 - margin / 100)),
            );
          } else {
            totalSkuCost = roundToMoney(cost / (1 - margin / 100)) * qty;
          }
          setValue(`cards[${ci}].totalSkuCost`, roundToMoney(totalSkuCost));
        } else if (costLock) {
          if (currentfield === "margin") {
            const rc = roundToMoney(cost / (1 - margin / 100));
            totalSkuCost = rc * qty;
            setValue(`cards[${ci}].customerCost`, rc);
          } else {
            margin = parseFloat(((customerCost - cost) / customerCost) * 100);
            totalSkuCost = roundToMoney(customerCost) * qty;
            setValue(`cards[${ci}].margin`, roundToMoney(margin));
          }
          setValue(`cards[${ci}].totalSkuCost`, roundToMoney(totalSkuCost));
        }
        totalBefore += roundToMoney(cost * qty);
        totalAfter += roundToMoney(totalSkuCost);
      } else {
        totalBefore += parseFloat(card.cost * card.qty) || 0;
        totalAfter += parseFloat(card.customerCost * card.qty) || 0;
      }
    });

    if (shippingCost > 0) {
      totalBefore += shippingCost;
      totalAfter += shippingCost;
    }
    if (tax > 0) {
      totalBefore += tax;
      totalAfter += tax;
    }

    setTotalCostBeforeMargin(roundToMoney(totalBefore));
    setTotalCostAfterMargin(roundToMoney(totalAfter));
    setValue("totalCostBeforeMargin", roundToMoney(totalBefore));
    setValue("totalCostAfterMargin", roundToMoney(totalAfter));
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* <ConfirmVendorPopup
        show={showVendorPopup}
        onClose={handleCloseVendorPopup}
        vendorName={pendingVendorName}
        onVendorAdded={handleVendorAdded}
      /> */}
      {/* <ConfirmProductPopup
        show={showProductPopup}
        onClose={handleCloseProductPopup}
        skuId={pendingSkuId}
        rfqId={opprId}
        vendorId={
          activeSkuIndex !== null
            ? watch(`cards[${activeSkuIndex}].vendorId`)
            : ""
        }
        productName={
          activeSkuIndex !== null
            ? watch(`cards[${activeSkuIndex}].productName`)
            : ""
        }
        description={
          activeSkuIndex !== null
            ? watch(`cards[${activeSkuIndex}].skuDescription`)
            : ""
        }
        aspettoCost={
          activeSkuIndex !== null ? watch(`cards[${activeSkuIndex}].cost`) : ""
        }
        customerPrice={
          activeSkuIndex !== null
            ? watch(`cards[${activeSkuIndex}].customerCost`)
            : ""
        }
        onProductAdded={handleProductAdded}
      /> */}

      <Modal
        scrollable
        show={show}
        onHide={onClose}
        dialogClassName="modal-90w"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header
          className="justify-content-center d-block text-center"
          closeButton
        >
          <Modal.Title className="tittle_deal">
            New Products/Vendors Found
          </Modal.Title>
          {/* <p className="mb-0">
            Some products or vendors are missing or not properly associated.
            Please review before marking this opportunity as WON.
          </p> */}
          <p className="mb-0 text-danger">
            Review the information and complete all mandatory fields before
            marking the opportunity as ‘Won.’ If the vendor name/SKU is
            highlighted as ‘New,’ double-check that there are no typos to avoid
            creating duplicates in the CRM.
          </p>
        </Modal.Header>

        {/* Pass onInvalid as the second arg so submitAttempted is set even when yup fails */}
        <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <Modal.Body>
            <div className="drag_section">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable-cards">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{ maxHeight: "600px", overflowY: "auto" }}
                    >
                      {cardFields.map((card, index) => (
                        <Draggable
                          key={card.id}
                          draggableId={`card-${index}`}
                          index={index}
                        >
                          {(provided) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className="mb-3"
                            >
                              <Card.Body>
                                <div className="grag_flex_row">
                                  {/* ── Drag / remove icon ── */}
                                  {/* <div className="drag_icon_set">
                                    <div
                                      className="drag_icon_group"
                                      {...provided.dragHandleProps}
                                    >
                                      {cardFields.length > 1 && (
                                        <IconCopyMinus
                                          onClick={() =>
                                            handleRemoveCard(index)
                                          }
                                        />
                                      )}
                                      <IconGridDots
                                        style={{ cursor: "move" }}
                                      />
                                    </div>
                                  </div> */}

                                  {/* ── VENDOR'S PARTNER ── */}
                                  <div className="mob_50">
                                    <Form.Group>
                                      <Form.Label className="required">
                                        VENDOR'S PARTNER
                                      </Form.Label>
                                      <Form.Control
                                        {...register(
                                          `cards[${index}].vendorPartner`,
                                        )}
                                        defaultValue={card.vendorPartner}
                                        placeholder="Enter Name"
                                        isInvalid={isVendorInvalid(index)}
                                        onChange={(e) =>
                                          handleSearchByName(e, index)
                                        }
                                        onFocus={() => handleInputFocus(index)}
                                        // onBlur={(e) =>
                                        //   handleAddVendor(e.target.value, index)
                                        // }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {vendorErrorMsg(index)}
                                      </Form.Control.Feedback>

                                      {/* Vendor suggestions dropdown */}
                                      {index === activeCardIndex &&
                                        suggestionData?.data?.length > 0 &&
                                        searchDebounces?.searchName?.length >
                                          0 && (
                                          <div
                                            className="suggestions_box"
                                            ref={(el) =>
                                              (vendorSuggestionRefs.current[
                                                index
                                              ] = el)
                                            }
                                          >
                                            <div className="table-responsive">
                                              <Table>
                                                <tbody>
                                                  {suggestionData.data.map(
                                                    (item, idx) => (
                                                      <tr
                                                        key={idx}
                                                        onClick={() =>
                                                          handleSelectVendor(
                                                            item,
                                                            index,
                                                          )
                                                        }
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                      >
                                                        <td>
                                                          {item?.display_name}
                                                        </td>
                                                      </tr>
                                                    ),
                                                  )}
                                                </tbody>
                                              </Table>
                                            </div>
                                          </div>
                                        )}
                                    </Form.Group>
                                  </div>

                                  {/* Hidden vendorId */}
                                  <Form.Control
                                    type="hidden"
                                    {...register(`cards[${index}].vendorId`)}
                                  />
                                  <Form.Control
                                    type="hidden"
                                    {...register(`cards[${index}].internalSku`)}
                                  />
                                  <Form.Control
                                    type="hidden"
                                    {...register(`cards[${index}].variantId`)}
                                  />

                                  {/* ── PRODUCT NAME ── */}
                                  <div className="mid_drag_column mob_50">
                                    <Form.Group>
                                      <Form.Label className="required">
                                        PRODUCT NAME
                                      </Form.Label>
                                      <Form.Control
                                        {...register(
                                          `cards[${index}].productName`,
                                        )}
                                        placeholder="Product Name"
                                        isInvalid={
                                          !!errors?.cards?.[index]?.productName
                                        }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {
                                          errors?.cards?.[index]?.productName
                                            ?.message
                                        }
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </div>

                                  {showSkuId && (
                                    <>
                                      <div className="mid_drag_column mob_50">
                                        <Form.Group>
                                          <Form.Label>UNIQUE SKU ID</Form.Label>
                                          <Form.Control
                                            {...register(
                                              `cards[${index}].skuIdUnique`,
                                            )}
                                            placeholder="SKU ID"
                                          />
                                        </Form.Group>
                                      </div>
                                      <div className="mid_drag_column mob_50">
                                        <Form.Group>
                                          <Form.Label>ROW ID</Form.Label>
                                          <Form.Control
                                            {...register(
                                              `cards[${index}].rowId`,
                                            )}
                                            placeholder="Row ID"
                                          />
                                        </Form.Group>
                                      </div>
                                    </>
                                  )}

                                  {/* ── SKU ID ── */}
                                  <div className="mid_drag_column mob_50">
                                    <Form.Group>
                                      <Form.Label className="required">
                                        SKU ID
                                      </Form.Label>
                                      <Form.Control
                                        {...register(`cards[${index}].skuId`)}
                                        placeholder="SKU ID"
                                        defaultValue={card.skuId}
                                        isInvalid={isSkuInvalid(index)}
                                        onChange={(e) =>
                                          handleSearchChange(
                                            activeCardIndex,
                                            "sku",
                                            e.target.value,
                                          )
                                        }
                                        onFocus={() => handleInputFocus(index)}
                                        // onBlur={(e) =>
                                        //   handleAddSku(e.target.value, index)
                                        // }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {skuErrorMsg(index)}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </div>
                                  <div className="mob_50">
                                    <Form.Group className="mb-3">
                                      <Form.Label className="required">
                                        SKU Type
                                      </Form.Label>
                                      <InputGroup>
                                        <Form.Select
                                          {...register(
                                            `cards[${index}].productType`,
                                          )}
                                        >
                                          <option value="non-inventory">
                                            Non-Inven
                                          </option>
                                          <option value="inventory">
                                            Inventory
                                          </option>
                                          <option value="kit">Kit</option>
                                        </Form.Select>
                                      </InputGroup>
                                      {errors.productType && (
                                        <span className="text-danger">
                                          {errors.productType.message}
                                        </span>
                                      )}
                                    </Form.Group>
                                  </div>
                                  {/* ── CLIN ── */}
                                  <div className="small_drag_column mob_50">
                                    <Form.Group>
                                      <Form.Label>CLIN</Form.Label>
                                      <Form.Control
                                        step="any"
                                        {...register(`cards[${index}].clin`)}
                                        placeholder="CLIN"
                                      />
                                    </Form.Group>
                                  </div>

                                  {/* ── SKU DESCRIPTION ── */}
                                  <div className="mob_100">
                                    <Form.Group>
                                      <Form.Label>SKU DESCRIPTION</Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={1}
                                        {...register(
                                          `cards[${index}].skuDescription`,
                                        )}
                                        defaultValue={card.skuDescription}
                                        placeholder="Description"
                                        ref={(el) => {
                                          textAreaRefs.current[index] = el;
                                        }}
                                        style={{
                                          resize: "none",
                                          overflow: "hidden",
                                        }}
                                        onChange={(e) => {
                                          handleSearchChangeDescription(
                                            index,
                                            "description",
                                            e.target.value,
                                            setValue(
                                              `cards[${index}].skuDescription`,
                                              e.target.value,
                                            ),
                                          );
                                          adjustTextAreaHeight(index);
                                        }}
                                        onFocus={() => handleInputFocus(index)}
                                      />
                                    </Form.Group>
                                  </div>

                                  {/* ── QTY ── */}
                                  <div className="mini_drag_column mob_50">
                                    <Form.Group>
                                      <Form.Label>QTY</Form.Label>
                                      <Form.Control
                                        type="text"
                                        onKeyDown={handleKeyDown}
                                        {...register(`cards[${index}].qty`)}
                                        defaultValue={card.qty}
                                        placeholder="0"
                                        isInvalid={
                                          !!errors?.cards?.[index]?.qty
                                        }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {errors?.cards?.[index]?.qty?.message}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </div>

                                  {/* ── UM ── */}
                                  <div className="mini_drag_column mob_50">
                                    <Form.Group>
                                      <Form.Label>UM</Form.Label>
                                      <Form.Control
                                        {...register(`cards[${index}].um`)}
                                        defaultValue={card.um}
                                        placeholder="EA"
                                        isInvalid={!!errors?.cards?.[index]?.um}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {errors?.cards?.[index]?.um?.message}
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </div>

                                  {/* ── ASPETTO COST ── */}
                                  <div className="mob_50">
                                    <Form.Group className="lock_group">
                                      <Form.Label>Aspetto Cost</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>
                                          <IconCurrencyDollar stroke={2} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          onKeyDown={handleKeyDown}
                                          {...register(`cards[${index}].cost`)}
                                          defaultValue={card.cost}
                                          placeholder="Enter Aspetto Cost"
                                          disabled={watch(
                                            `cards[${index}].CostLock`,
                                          )}
                                          isInvalid={
                                            !!errors?.cards?.[index]?.cost
                                          }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {
                                            errors?.cards?.[index]?.cost
                                              ?.message
                                          }
                                        </Form.Control.Feedback>
                                      </InputGroup>
                                      <span className="lock_unlock">
                                        <Form.Check
                                          type="switch"
                                          id={`lock-toggle-${index}`}
                                          label={
                                            watch(
                                              `cards[${index}].CostLock`,
                                            ) ? (
                                              <IconLock stroke={1} />
                                            ) : (
                                              <IconLockOpen2 stroke={1} />
                                            )
                                          }
                                          {...register(
                                            `cards[${index}].CostLock`,
                                          )}
                                          checked={watch(
                                            `cards[${index}].CostLock`,
                                          )}
                                          onChange={() =>
                                            handleLockChange("CostLock", index)
                                          }
                                        />
                                      </span>
                                    </Form.Group>
                                  </div>

                                  {/* ── MARGIN ── */}
                                  <div className="small_drag_column mob_50">
                                    <Form.Group className="lock_group">
                                      <Form.Label>Margin</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>
                                          <IconPercentage stroke={2} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          onKeyDown={handleKeyDown}
                                          {...register(
                                            `cards[${index}].margin`,
                                          )}
                                          onChange={(e) =>
                                            handleMarginChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          defaultValue={card.margin}
                                          placeholder="0.00"
                                          disabled={watch(
                                            `cards[${index}].marginLock`,
                                          )}
                                          isInvalid={
                                            !!errors?.cards?.[index]?.margin
                                          }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {
                                            errors?.cards?.[index]?.margin
                                              ?.message
                                          }
                                        </Form.Control.Feedback>
                                      </InputGroup>
                                      <span className="lock_unlock">
                                        <Form.Check
                                          type="switch"
                                          id={`margin-lock-toggle-${index}`}
                                          label={
                                            watch(
                                              `cards[${index}].marginLock`,
                                            ) ? (
                                              <IconLock stroke={1} />
                                            ) : (
                                              <IconLockOpen2 stroke={1} />
                                            )
                                          }
                                          {...register(
                                            `cards[${index}].marginLock`,
                                          )}
                                          checked={watch(
                                            `cards[${index}].marginLock`,
                                          )}
                                          onChange={() =>
                                            handleLockChange(
                                              "marginLock",
                                              index,
                                            )
                                          }
                                        />
                                      </span>
                                    </Form.Group>
                                  </div>

                                  {/* ── CUSTOMER PRICE ── */}
                                  <div className="mob_50">
                                    <Form.Group className="lock_group">
                                      <Form.Label>Customer Price</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>
                                          <IconCurrencyDollar stroke={2} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          type="text"
                                          onKeyDown={handleKeyDown}
                                          {...register(
                                            `cards[${index}].customerCost`,
                                          )}
                                          defaultValue={card.customerCost}
                                          placeholder="Customer's Cost"
                                          disabled={watch(
                                            `cards[${index}].customerCostLock`,
                                          )}
                                          isInvalid={
                                            !!errors?.cards?.[index]
                                              ?.customerCost
                                          }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                          {
                                            errors?.cards?.[index]?.customerCost
                                              ?.message
                                          }
                                        </Form.Control.Feedback>
                                      </InputGroup>
                                      <span className="lock_unlock">
                                        <Form.Check
                                          type="switch"
                                          id={`customer-cost-lock-toggle-${index}`}
                                          label={
                                            watch(
                                              `cards[${index}].customerCostLock`,
                                            ) ? (
                                              <IconLock stroke={1} />
                                            ) : (
                                              <IconLockOpen2 stroke={1} />
                                            )
                                          }
                                          {...register(
                                            `cards[${index}].customerCostLock`,
                                          )}
                                          checked={watch(
                                            `cards[${index}].customerCostLock`,
                                          )}
                                          onChange={() =>
                                            handleLockChange(
                                              "customerCostLock",
                                              index,
                                            )
                                          }
                                        />
                                      </span>
                                    </Form.Group>
                                  </div>

                                  {/* ── TOTAL SKU PRICE (read-only) ── */}
                                  <div className="mob_50">
                                    <Form.Group>
                                      <Form.Label>Total SKU Price</Form.Label>
                                      <InputGroup>
                                        <InputGroup.Text>
                                          <IconCurrencyDollar stroke={2} />
                                        </InputGroup.Text>
                                        <Form.Control
                                          readOnly
                                          type="text"
                                          {...register(
                                            `cards[${index}].totalSkuCost`,
                                          )}
                                          defaultValue={card.totalSkuCost}
                                          placeholder="Total SKU Cost"
                                        />
                                      </InputGroup>
                                    </Form.Group>
                                  </div>
                                </div>
                                {cardSearch[index]?.showSuggestions &&
                                  (cardSearch[index]?.vendor ||
                                    cardSearch[index]?.sku ||
                                    cardSearch[index]?.description) &&
                                  dataSuggetion?.data?.length > 0 && (
                                    <div
                                      className="suggestions_box"
                                      ref={(el) =>
                                        (suggestionBoxRefs.current[index] = el)
                                      }
                                    >
                                      <div className="table-responsive">
                                        <Table>
                                          <tbody>
                                            {dataSuggetion.data.map(
                                              (product, idx) => (
                                                <tr
                                                  key={idx}
                                                  onClick={() =>
                                                    handleProductSelect(
                                                      product,
                                                      index,
                                                    )
                                                  }
                                                  style={{
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <td>
                                                    {
                                                      product?.Vendor
                                                        ?.display_name
                                                    }
                                                  </td>
                                                  <td>
                                                    {product?.sku_display_name}
                                                  </td>
                                                  <td>{product?.name}</td>
                                                  <td>
                                                    {trimText(
                                                      product?.description_sales,
                                                      100,
                                                    )}
                                                  </td>
                                                  <td>{product?.uoi}</td>
                                                  <td>
                                                    ${trimTrailingZeros(product?.aspetto_price)}
                                                  </td>
                                                  <td>
                                                    {trimTrailingZeros(product?.margin_percent)}%
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </div>
                                  )}
                              </Card.Body>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </Modal.Body>

          <Modal.Footer className="justify-content-center">
            <Button
              style={{ minWidth: "auto" }}
              variant="outline-secondary"
              onClick={() => navigate(`/edit-rfq/${rfqId}`)}
            >
              Edit Opportunity
            </Button>
            <Button variant="warning" type="submit">
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default RfqWonPopup;
